import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FieldTitle from './FieldTitle';
import FieldInput from './FieldInput';

import Tooltip from '../../misc/Tooltip';


const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

const StyledInternalWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right:0;
  top:0;
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class TextField extends React.PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldPresentationMode: PropTypes.string,
    pageLayoutMode: PropTypes.string,

    type: PropTypes.string,
    title: PropTypes.string,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,
    tipVisible: PropTypes.bool,
    disabled: PropTypes.bool,
    tabIndex: PropTypes.number,

    wrapperStyles: PropTypes.object,

    //SDK Styles
    primaryColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ])

  }

  static defaultProps = {
    value: '',
    wrapperStyles: {}
  }

  constructor(props) {
    super(props);
    const { value } = props;
    this.state = { focused: false, changes: value };
  }

  componentDidUpdate(prevProps) {
    const { changes } = this.state;
    const { value } = this.props;
    if (value !== prevProps.value && value !== changes) this.setState({ changes: value });

  }

  componentWillUnmount() {

    const { value, disabled, onChange, fieldId, fieldIdentifier, fieldPositionId, onBlur } = this.props;
    const { changes, focused } = this.state;

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== value && !disabled) {
      if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, changes);
      if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

    }

  }

  render() {

    const { focused, changes } = this.state;

    const {

      wrapperStyles,

      fieldId,
      fieldIdentifier,
      fieldPositionId,

      type,
      title,
      tipTitle,
      tipDescription,
      tipVisible,
      value,
      disabled,
      tabIndex,

      primaryColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,

      //Action handlers
      onChange,
      onFocus,
      onBlur,
      placeholder,
      fieldMode,
      pageLayoutMode,
      fieldPresentationMode,

    } = this.props;

    const getTooltip = () => {

      if (disabled) return null;

      return (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      );

    };
    return (
      <StyledWrapper {...{ margin }} style={wrapperStyles || {}}>
        <StyledInternalWrapper>
          <FieldTitle
            {...{
              title,
              titleDisplay,
              titleFontSize,
              titleFontColor,
              titleTextAlign,
              titleFontStyle,
              titleFontWeight,
              titleTextTransform,
              titleTextDecoration,
            }}
          />
          {getTooltip()}
          <FieldInput
            data-testid={`${fieldPositionId}-text-field`}
            tabIndex={tabIndex}
            value={getDisplayValue(type, changes)}
            type={getFieldInputType(disabled, type)}
            placeholder={placeholder}
            {...{
              disabled,
              focused,
              fontSize,
              fontColor,
              fontStyle,
              fontWeight,
              textAlign,
              textTransform,
              textDecoration,
              textOverflow,
              borderColor,
              borderRadius,
              borderWidth,
              backgroundColor,
              padding,
              primaryColor,
              fieldId,
              fieldMode,
              pageLayoutMode,
              fieldPresentationMode,
            }}
            onChange={(e) => {
              this.setState({ changes: getChangeValue(type, e.target.value) });
            }}
            onFocus={(e) => {

              if (!focused) this.setState({ focused: true });

              if (!disabled && onFocus && !focused) {
                onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });
              }

            }}
            onBlur={(e) => {

              if (focused) this.setState({ focused: false });

              if (!disabled && value !== changes) onChange(fieldId, fieldIdentifier, fieldPositionId, changes);

              if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

            }}
          />
        </StyledInternalWrapper>
      </StyledWrapper>
    );
  }
}

export const getFieldInputType = (disabled, type) => {
  return !disabled ? type : 'text';
};

export const getDisplayValue = (type, value) => {

  /**
   * <----------------------------NOTICE------------------------->
   * Text field component and functions inside this file are used in
   * many places.
   *
   * 0 is technically considered as a falsy value (!value) but, we have
   * value!==0 here so we could display 0 as the default value for xMin
   * and ymin in the chartFieldForm.
   *
   */
  if (!value && value !== 0) {
    return '';
  }
  else if (type === 'number' && !Number.isNaN(value)) {
    return value;
  }
  else if (value) {
    return value;
  }

};

export const getChangeValue = (type, value) => {

  let nextValue = value;

  if (type === 'number' && typeof nextValue === 'string' && nextValue !== '') {
    /**
     * This was to prevent the number from being saved as a string on the db
     */
    nextValue = nextValue.indexOf('.') === -1 ? parseInt(nextValue, 10) : parseFloat(nextValue, 10);

  }

  return nextValue;

};

export default TextField;
